/**
 * Pure helpers for the Playbook + Close Skills founding price (no imports, so the node tests in
 * supabase/functions/library-prerender/founding.test.mts can load this file directly).
 * Whether the offer is live comes only from src/lib/foundingOffer.ts (the create-playbook-checkout status call):
 * every helper here takes that answer, and a null answer means full price.
 */

export type FoundingOffer = { price_cents: number; list_cents: number; ends_at: string };

/** The OfferKey of the bundle in src/lib/offers.ts. */
export const FOUNDING_OFFER_KEY = "close_playbook_skills";

export const dollars = (c: number) => `$${(c / 100).toFixed(c % 100 ? 2 : 0)}`;

/** "November 21": the PT calendar day the offer ends. */
export function endDay(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: "America/Los_Angeles" });
  } catch {
    return "November 21";
  }
}

/** "2026-11-21": the PT calendar day the offer ends, for schema.org priceValidUntil. */
export function foundingValidUntil(o: FoundingOffer): string {
  try {
    return new Date(o.ends_at).toLocaleDateString("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "America/Los_Angeles" });
  } catch {
    return o.ends_at.slice(0, 10);
  }
}

/** "Founding price: $79 for the first 50 buyers, until November 21. Then $99." */
export function foundingLine(o: FoundingOffer): string {
  return `Founding price: ${dollars(o.price_cents)} for the first 50 buyers, until ${endDay(o.ends_at)}. Then ${dollars(o.list_cents)}.`;
}

type OfferCard = { key: string; kicker: string; body: string; price: string | null; line?: string };

/**
 * A cross-promo card (src/lib/offers.ts OFFERS) with the founding price applied, for the bundle only and only
 * when `founding` is the live answer. Any other offer, or no live answer, comes back unchanged.
 */
export function offerWithFounding<T extends OfferCard>(offer: T, founding: FoundingOffer | null): T {
  if (!founding || offer.key !== FOUNDING_OFFER_KEY) return offer;
  const now = dollars(founding.price_cents);
  const line = foundingLine(founding);
  return {
    ...offer,
    kicker: `Playbook + Close Skills, ${now} founding price`,
    body: `${offer.body} ${line}`,
    price: now,
    ...(offer.line ? { line: `${offer.line} ${line}` } : {}),
  };
}
